'use client';

import { useMemo } from 'react';
import {
  AlertTriangle,
  RefreshCw,
  ShieldAlert,
  WifiOff,
  Wrench,
  FileWarning,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { SectionLabel } from '@/components/ui/section-label';
import { ErrorSupportDetails } from '@/components/observability/ErrorSupportDetails';
import {
  Empty,
  EmptyActions,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import {
  extractErrorMetadata,
  getErrorStateContent,
  type FrontendErrorKind,
} from '@/lib/errors/error-experience';
import { generateErrorRef } from '@/lib/errors/sanitize-error';
import { isDevelopment } from '@/lib/env';
import { cn } from '@/lib/utils';

interface ErrorStateProps {
  className?: string;
  error: unknown;
  eventId?: string | null;
  homeHref?: string;
  homeLabel?: string;
  onRetry?: () => void;
  onReport?: () => void;
  reportAction?: string;
  sectionLabel?: string;
  supportHref?: string;
  viewport?: 'inline' | 'screen';
}

const kindAppearance: Record<
  FrontendErrorKind,
  {
    icon: typeof FileWarning;
    iconClassName: string;
    mediaClassName: string;
  }
> = {
  validation: {
    icon: FileWarning,
    iconClassName: 'text-warning-500',
    mediaClassName: 'bg-warning-500/10',
  },
  network: {
    icon: WifiOff,
    iconClassName: 'text-info-500',
    mediaClassName: 'bg-info-500/10',
  },
  permission: {
    icon: ShieldAlert,
    iconClassName: 'text-warning-500',
    mediaClassName: 'bg-warning-500/10',
  },
  dependency: {
    icon: Wrench,
    iconClassName: 'text-accent-500',
    mediaClassName: 'bg-accent-500/10',
  },
  unexpected: {
    icon: AlertTriangle,
    iconClassName: 'text-danger-500',
    mediaClassName: 'bg-danger-500/10',
  },
};

function buildSignInHref(): string {
  if (typeof window === 'undefined') {
    return '/sign-in';
  }

  const returnUrl = `${window.location.pathname}${window.location.search}`;
  return `/sign-in?redirect_url=${encodeURIComponent(returnUrl)}`;
}

function navigateTo(href: string) {
  if (typeof window !== 'undefined') {
    window.location.href = href;
  }
}

export function ErrorState({
  className,
  error,
  eventId,
  homeHref,
  homeLabel = 'Go Home',
  onRetry,
  onReport,
  reportAction,
  sectionLabel,
  supportHref,
  viewport = 'inline',
}: ErrorStateProps) {
  const state = getErrorStateContent(error, { sectionLabel });
  const metadata = extractErrorMetadata(error);
  const supportRef = useMemo(() => {
    if (state.kind !== 'unexpected') {
      return undefined;
    }

    return state.supportRef || generateErrorRef();
  }, [state.kind, state.supportRef]);
  const appearance = kindAppearance[state.kind];
  const Icon = appearance.icon;
  const showSupportAction = Boolean(supportHref && state.kind === 'unexpected');
  const showDebugPanel = isDevelopment && error instanceof Error;

  const handleGoBack = () => {
    if (typeof window === 'undefined') {
      return;
    }

    if (window.history.length > 1) {
      window.history.back();
      return;
    }

    if (homeHref) {
      navigateTo(homeHref);
    }
  };

  return (
    <div
      className={cn(
        viewport === 'screen'
          ? 'bg-background flex min-h-screen items-center justify-center px-4 py-10'
          : 'flex min-h-[420px] items-center justify-center p-6',
        className
      )}
    >
      <Card className="border-border/70 bg-surface shadow-elevation-1 w-full max-w-2xl min-w-0 overflow-hidden">
        <div className="p-6 sm:p-8">
          <Empty className="min-w-0 items-start text-left">
            <EmptyHeader className="w-full min-w-0 items-start text-left">
              <EmptyMedia
                className={cn(
                  'size-14 rounded-2xl',
                  appearance.mediaClassName,
                  appearance.iconClassName
                )}
              >
                <Icon aria-hidden="true" />
              </EmptyMedia>
              <div className="min-w-0 space-y-2">
                <EmptyTitle className="text-2xl break-words">
                  {state.title}
                </EmptyTitle>
                <EmptyDescription className="max-w-2xl text-base leading-7 break-words">
                  {state.description}
                </EmptyDescription>
              </div>
            </EmptyHeader>

            <div className="border-border/70 bg-muted/30 mt-6 w-full min-w-0 rounded-2xl border p-4">
              <SectionLabel>Next step</SectionLabel>
              <p className="text-foreground mt-2 text-sm leading-6 break-words">
                {state.nextAction}
              </p>
            </div>

            <ErrorSupportDetails
              eventId={eventId}
              requestId={state.requestId}
              supportCode={supportRef}
              onReport={onReport}
              reportAction={reportAction}
            />

            {showDebugPanel && (
              <div className="border-danger-500/20 bg-danger-500/5 mt-4 w-full rounded-2xl border p-4 text-xs">
                <p className="text-danger-500 font-mono break-words">
                  {error.message}
                </p>
                <div className="text-muted-foreground mt-2 space-y-1 font-mono">
                  {metadata.status ? <p>Status: {metadata.status}</p> : null}
                  {metadata.code ? <p>Code: {metadata.code}</p> : null}
                  {metadata.digest ? <p>Digest: {metadata.digest}</p> : null}
                </div>
              </div>
            )}

            <EmptyActions className="justify-start pt-6">
              {state.authRequired ? (
                <Button onClick={() => navigateTo(buildSignInHref())}>
                  Sign In
                </Button>
              ) : state.canRetry && onRetry ? (
                <Button onClick={onRetry}>
                  <RefreshCw className="mr-2 size-4" />
                  {state.retryLabel}
                </Button>
              ) : (
                <Button onClick={handleGoBack}>Go Back</Button>
              )}

              {homeHref ? (
                <Button variant="outline" onClick={() => navigateTo(homeHref)}>
                  {homeLabel}
                </Button>
              ) : null}

              {showSupportAction ? (
                <Button
                  variant="ghost"
                  onClick={() => navigateTo(supportHref!)}
                >
                  Open Support
                </Button>
              ) : null}
            </EmptyActions>
          </Empty>
        </div>
      </Card>
    </div>
  );
}
